import axios from 'axios';
import authHeader from './user-auth-header';

const API_URL = '/user/';

class UserService {

    /**
     * Sends a booking order to the server.
     *
     * @param {Object} booking - The booking details to be sent.
     * @returns {Promise<Object|boolean>} The response data from the server if successful, otherwise false.
     */
    async postCommande(booking) {
        if (!booking) return false;
        try {
            let res = await axios.post(API_URL + 'commande/post.php', booking);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async postAmendCommande(amend_commande) {
        if (!amend_commande) return false;
        try {
            let res = await axios.post(API_URL + 'commande/amend_options/post.php', amend_commande);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async postTransfertInscription(booking, transfered_inscription) {
        if (!booking || !transfered_inscription) return false;
        try {
            let data = {
                reservation: booking,
                transferable_inscription: transfered_inscription
            }
            let res = await axios.post(API_URL + 'inscription/accept_transfert/post.php', data);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Checks if a booking has already been paid.
     *
     * @param {Object} booking - The booking object to check.
     * @returns {Promise<boolean>} - A promise that resolves to true if the booking has already been paid, otherwise false.
     */
    async checkIfCommandeAlreadyPaid(booking) {
        if (!booking) return false;
        try {
            let res = await axios.post(API_URL + 'commande/check_if_already_paid.php', booking);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }


    /**
     * Fetches a specific commande by its ID.
     *
     * @param {number|string} id_commande - The ID of the commande to fetch.
     * @returns {Promise<Object|boolean>} - A promise that resolves to the commande data if successful, or false if an error occurs or the ID is not provided.
     */
    async getCommande(id_commande) {
        if (!id_commande) return false;
        try {
            let res = await axios.get(API_URL + 'commande/get.php?id_commande=' + id_commande, { headers: authHeader() });
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async getCommandeByCryptedUUID(uuid) {
        if (!uuid) return false;
        try {
            let res = await axios.get(API_URL + 'commande/get.php?uuid=' + uuid, { headers: authHeader() });
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Fetches the list of coureurs from the API.
     *
     * @async
     * @function getCoureurs
     * @returns {Promise<Array>} A promise that resolves to an array of coureurs. If an error occurs, returns an empty array.
     */
    async getCoureurs() {
        try {
            let res = await axios.get(API_URL + 'coureurs/get.php', { headers: authHeader() });
            return res.data;
        }
        catch (err) {
            return [];
        }
    }


    async getAdminToken(base_module) {
        if (!base_module) return false;
        try {
            let res = await axios.get(API_URL + 'module/get_admin_token.php?base_module=' + base_module, { headers: authHeader() });
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Retrieves ticket URLs for a given transaction.
     *
     * @param {Object} transaction - The transaction object containing order information.
     * @param {Object} transaction.commande_infos - The order information object.
     * @param {string} transaction.commande_infos.orderId - The unique identifier for the order.
     * @returns {Promise<Object|boolean>} - A promise that resolves to the ticket URLs data or false if the transaction is invalid.
     */
    async getUrlsBillets(transaction) {
        if (!transaction) return false;
        if (!transaction.commande_infos) return false;
        if (!transaction.commande_infos.orderId) return false;
        const data = { uuid: transaction.commande_infos.orderId };
        try {
            let res = await axios.post(API_URL + 'commande/get_tickets_links.php', data, { headers: authHeader() });
            return res.data;
        }
        catch (err) {
            return [];
        }
    }



    /* Contenu de la fiche d'un compte pour un module */
    async getFiche(id_module) {
        if (!id_module) return;
        try {
            let res = await axios.get(API_URL + 'fiche/get.php?id_module=' + id_module, { headers: authHeader() });
            return res.data;
        }
        catch (err) {
            return {};
        }
    }

    async getRegisterByOtherUser(mail, id_module, id_user) {
        if (!mail) return false;
        if (!id_module) return false;
        if (!id_user) return false;
        let data = {
            mail: mail,
            id_module: id_module,
            id_user: id_user
        }
        try {
            let res = await axios.post(API_URL + 'fiche/get_register_by_other_user.php', data, { headers: authHeader() });
            if (res.data) {
                return true;
            } else {
                return false;
            }
        }
        catch (err) {
            return false;
        }
    }

    /* Une inscription */
    async getInscription(id_inscription) {
        if (!id_inscription) return false;
        try {
            let res = await axios.get(API_URL + 'inscription/get.php?id_inscription=' + id_inscription, { headers: authHeader() });
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /* Les numéros de téléphone de vos accompagnants */
    async getAlerteSMSContacts(id_inscription) {
        if (!id_inscription) return false;
        try {
            let res = await axios.get(API_URL + 'inscription/alerte_sms_contacts/get.php?id_inscription=' + id_inscription, { headers: authHeader() });
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /* Une inscription par son token */
    async getInscriptionByToken(token) {
        if (!token) return false;
        try {
            let res = await axios.get(API_URL + 'inscription/get_by_token.php?token=' + token);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /* Une commande inscription */
    async getCommandeInscription(id_commande_inscription) {
        if (!id_commande_inscription) return false;
        try {
            let res = await axios.get(API_URL + 'commande_inscription/get.php?id_commande_inscription=' + id_commande_inscription, { headers: authHeader() });
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /* Met a jour les champs d'infos d'une inscription */
    async updateInscriptionInformations(inscription) {
        if (!inscription) return false;
        try {
            let res = await axios.post(API_URL + 'inscription/informations/update.php', inscription, { headers: authHeader() });
            return res.data;
        }
        catch (err) {
            return err.response.data;
        }
    }

    /* Met a jour les champs d'infos d'une inscription par son token */
    async updateInscriptionInformationsByToken(token, inscription) {
        if (!token || !inscription) return false;
        try {
            let data = {
                token: token,
                inscription: inscription
            }
            let res = await axios.post(API_URL + 'inscription/informations/update_by_token.php', data);
            return res.data;
        }
        catch (err) {
            return err.response.data;
        }
    }


    /* Met a jour le document d'une inscription */
    async updateInscriptionDocument(inscription) {
        if (!inscription) return false;
        try {
            let res = await axios.post(API_URL + 'inscription/document/update.php', inscription, { headers: authHeader() });
            return res.data;
        }
        catch (err) {
            return err.response.data;
        }
    }

    /* Met a jour le document d'une inscription par son token */
    async updateInscriptionDocumentByToken(token, inscription) {
        if (!token || !inscription) return false;
        try {
            let data = {
                token: token,
                inscription: inscription
            }
            let res = await axios.post(API_URL + 'inscription/document/update_by_token.php', data);
            return res.data;
        }
        catch (err) {
            return err.response.data;
        }
    }

    /* Envoi la demande de transfert d'une inscription */
    async sendTransfertRequest(request) {
        if (!request) return false;
        try {
            await axios.post(API_URL + 'inscription/transfert/post.php', request, { headers: authHeader() });
            return true;
        }
        catch (err) {
            return err.response.data;
        }
    }

    /* Envoi la demande de remboursement GIA d'une inscription */
    async refundWithGIA(refund) {
        if (!refund) return false;
        try {
            let request = await axios.post(API_URL + 'inscription/refund/post.php', refund, { headers: authHeader() });
            return request;
        }
        catch (err) {
            console.log(err);
            return false;
        }
    }

    /* Récupere la liste des demandes de transfert liées à une inscription */
    async getTransfertInscriptionRequests(id_inscription) {
        if (!id_inscription) return [];
        try {
            let res = await axios.get(API_URL + 'inscription/transferts/get.php?id_inscription=' + id_inscription, { headers: authHeader() });
            return res.data;
        }
        catch (err) {
            return [];
        }
    }

    /* Envoi le mail de confirmation de commande à l'utilisateur */
    async sendCommandeConfirmationEmail(uuid_commande) {
        if (!uuid_commande) return "Une erreur est survenue";
        try {
            await axios.post(API_URL + 'commande/email/send.php', { uuid_commande: uuid_commande }, { headers: authHeader() });
            return "Email de confirmation renvoyé";
        }
        catch (err) {
            return err.response.data.error;
        }
    }

    /* Envoi le mail d'inscription à l'inscrit */
    async sendInscriptionEmail(id_inscription) {
        if (!id_inscription) return "Une erreur est survenue";
        try {
            await axios.post(API_URL + 'inscription/email/send.php', { id_inscription: id_inscription }, { headers: authHeader() });
            return "Email envoyé avec succès";
        }
        catch (err) {
            return err.response.data.error;
        }
    }

    /* Supprime une inscription */
    async deleteInscription(id_inscription) {
        if (!id_inscription) return false;
        try {
            await axios.get(API_URL + 'inscription/delete.php?id_inscription=' + id_inscription, { headers: authHeader() });
            return true;
        }
        catch (err) {
            return false;
        }
    }



    /**
     * Checks if the current user is the owner of a given resource.
     *
     * @param {string} ressource - The type of resource to check ownership for.
     * @param {number|string} id_ressource - The ID of the resource to check ownership for.
     * @returns {Promise<boolean>} - A promise that resolves to true if the user is the owner, otherwise false.
     */
    async isOwner(ressource, id_ressource) {
        if (!ressource || !id_ressource) return false;
        try {
            let res = await axios.get(API_URL + 'is_owner.php?ressource=' + ressource + '&id_ressource=' + id_ressource, { headers: authHeader() });
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Updates the PPS number for a given inscription.
     *
     * @param {string} id_inscription - The ID of the inscription to update.
     * @param {string} numero_pps - The new PPS number to set.
     * @returns {Promise<Object|boolean>} - Returns the response data from the server if successful, or false if the input parameters are invalid.
     * @throws {Object} - Throws an error object containing the response data if the request fails.
     */
    async updatePPS(id_inscription, numero_pps) {
        if (!id_inscription || !numero_pps) return false;
        try {
            let res = await axios.post(API_URL + 'inscription/pps/update.php', { id: id_inscription, numero_pps: numero_pps }, { headers: authHeader() });
            return res.data;
        }
        catch (err) {
            return err.response.data;
        }
    }

    async sendBourseDossardRequest(id_inscription, id_module, refundable_price) {
        if (!id_inscription || !id_module) return false;
        try {
            let res = await axios.post(API_URL + 'inscription/bourse_dossards/post.php', { id_inscription: id_inscription, id_module: id_module, refundable_amount: refundable_price }, { headers: authHeader() });
            return res.data;
        }
        catch (err) {
            return err.response.data;
        }
    }

    async sendBourseDossardRequestEquipe(id_inscription, id_module, refundable_price) {
        if (!id_inscription || !id_module) return false;
        try {
            let res = await axios.post(API_URL + 'inscription/bourse_dossards/post_equipe.php', { id_inscription: id_inscription, id_module: id_module, refundable_amount: refundable_price }, { headers: authHeader() });
            return res.data;
        }
        catch (err) {
            return err.response.data;
        }

    }

    async saveAlerteSMSContacts(alerte_sms_contacts) {
        if (!alerte_sms_contacts) return false;
        try {
            let res = await axios.post(API_URL + 'inscription/alerte_sms_contacts/update.php', alerte_sms_contacts, { headers: authHeader() });
            return res.data;
        }

        catch (err) {
            return false;
        }
    }

    async getEquipeInscriptions(id_inscription) {
        if (!id_inscription) return false;
        try {
            let res = await axios.get(API_URL + 'inscription/equipe/get.php?id_inscription=' + id_inscription, { headers: authHeader() });
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async isLCE26Top5Performance(performance) {
        if (!performance) return false;
        try {
            let res = await axios.get(API_URL + 'hooks/lce26_top_performance.php?current_performance=' + performance, { headers: authHeader() });

            if (res.data?.is_top_performance) {
                return true;
            } else {
                return false;
            }
        }
        catch (err) {
            return false;
        }
    }

    async updateInscriptionChamp(id_inscription, champ, value) {
        if (!id_inscription || !champ || !value) return false;
        try {
            let res = await axios.post(API_URL + 'inscription/champ/update.php', { id_inscription: id_inscription, champ: champ, value: value }, { headers: authHeader() });
            return res.data;
        }
        catch (err) {
            return false;
        }

    }

    /** Même endpoint que updateInscriptionChamp, authentification par token (lien magique). */
    async updateInscriptionChampByToken(token, id_inscription, champ, value) {
        if (!token || !id_inscription || !champ || !value) return false;
        try {
            let res = await axios.post(API_URL + 'inscription/champ/update.php', {
                token,
                id_inscription,
                champ,
                value,
            });
            return res.data;
        } catch (err) {
            return err.response?.data ?? false;
        }
    }
}

export default new UserService();